import {
  cn,
  Selection,
  SortDescriptor,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import React from 'react';
import type { ColumnSize } from '@react-types/table';
import { useMediaQuery } from 'usehooks-ts';
import { DEFAULT_MAX_HEIGHT, DEFAULT_PAGE_SIZE } from './constants';
import { BottomContent } from './Footer';
import { TopContent } from './Header';
import { getSelectAllTargetUids } from './filter-utils';
import { TableConfig } from './types';
import { applySorting } from './utils';
import VirtualizedTable from './virtualized-table';

function isSelectionSet(
  selection: Selection | undefined
): selection is Exclude<Selection, 'all'> {
  return selection != null && selection !== 'all';
}

function getSelectionMode(
  config: TableConfig
): 'none' | 'single' | 'multiple' {
  if (config.actions?.enableRowSelection) {
    return 'multiple';
  }

  return config.actions?.defaultRowSelection ?? 'none';
}

function getColumnWidth(
  column: TableConfig['columns'][number],
  columnCount: number,
  isMobile: boolean,
  useAutoLayout: boolean
): ColumnSize | undefined {
  if (isMobile) {
    return undefined;
  }

  if (useAutoLayout) {
    return column.width ? (column.width as ColumnSize) : undefined;
  }

  if (column.width) {
    return column.width as ColumnSize;
  }

  return `${Math.round(100 / Math.max(columnCount, 1))}%` as `${number}%`;
}

export default function DataTable<T extends { id: string | number }>({
  config,
  data,
  onSelectionChange,
  isLoading,
  selectedKeys: controlledSelectedKeys,
}: {
  config: TableConfig;
  data: T[];
  onSelectionChange?: (keys: Selection) => void;
  isLoading?: boolean;
  selectedKeys?: Selection;
}) {
  // States
  const [filterValue, setFilterValue] = React.useState('');
  const [internalSelectedKeys, setInternalSelectedKeys] =
    React.useState<Selection>(new Set([]));
  const selectedKeys = controlledSelectedKeys ?? internalSelectedKeys;
  const [visibleColumns, setVisibleColumns] = React.useState<Selection>(
    new Set(
      config.initialVisibleColumns || config.columns.map((col) => col.uid)
    )
  );
  const columnUidsKey = config.columns.map((column) => column.uid).join('|');
  // Column uids from the previous render, so only columns that are genuinely
  // new get shown. Columns left out of `initialVisibleColumns` stay hidden.
  const previousColumnUidsRef = React.useRef<string[]>(
    config.columns.map((column) => column.uid)
  );

  React.useEffect(() => {
    const nextUids = config.columns.map((column) => column.uid);
    const previousUids = previousColumnUidsRef.current;
    previousColumnUidsRef.current = nextUids;

    setVisibleColumns((current) => {
      if (current === 'all') {
        return current;
      }

      const currentUids = Array.from(current, String);
      const keptUids = currentUids.filter((uid) => nextUids.includes(uid));
      const addedUids = nextUids.filter(
        (uid) => !previousUids.includes(uid) && !currentUids.includes(uid)
      );

      if (keptUids.length === currentUids.length && addedUids.length === 0) {
        return current;
      }

      const nextVisible = [...keptUids, ...addedUids];
      // Columns were replaced wholesale: fall back to showing all of them.
      return new Set(nextVisible.length > 0 ? nextVisible : nextUids);
    });
  }, [columnUidsKey, config.columns]);
  const [filters, setFilters] = React.useState<Record<string, Selection>>({});
  const userModifiedFiltersRef = React.useRef<Set<string>>(new Set());
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    config.pagination?.initialRowsPerPage || DEFAULT_PAGE_SIZE
  );
  const [sortDescriptor, setSortDescriptor] = React.useState<
    SortDescriptor | undefined
  >(
    config.disableDefaultSort
      ? undefined
      : config.initialSortDescriptor ?? {
          column: config.columns[0]?.uid || '',
          direction: 'ascending',
        }
  );

  // Memoized values
  const hasSearchFilter = Boolean(filterValue);

  // Ensure data is always an array
  const safeData = React.useMemo(() => {
    return Array.isArray(data) ? data : [];
  }, [data]);

  const headerColumns = React.useMemo(() => {
    return config.columns.filter((column) =>
      Array.from(visibleColumns).includes(column.uid)
    );
  }, [visibleColumns, config.columns]);

  const filteredItems = React.useMemo(() => {
    let filteredData = [...safeData];

    // Search filter
    if (hasSearchFilter && config.searchKey) {
      filteredData = filteredData.filter((item) =>
        String(item[config.searchKey as keyof T])
          .toLowerCase()
          .includes(filterValue.toLowerCase())
      );
    }

    // Custom filters
    config.filters?.forEach((filterConfig) => {
      const values = filters[filterConfig.key];
      if (!isSelectionSet(values) || values.size === 0) {
        if (filterConfig.emptySelectionShowsNone) {
          filteredData = [];
        }
        return;
      }

      if (filterConfig.matchFilter) {
        filteredData = filteredData.filter((item) =>
          filterConfig.matchFilter!(item, values)
        );
        return;
      }

      filteredData = filteredData.filter((item) =>
        Array.from(values).includes(String(item[filterConfig.key as keyof T]))
      );
    });

    return filteredData;
  }, [safeData, filterValue, filters, hasSearchFilter, config.searchKey, config.filters]);

  // Remove the local pagination slicing
  const items = React.useMemo(() => {
    return filteredItems;
  }, [filteredItems]);

  // Use server-side sorting if available, otherwise do client-side
  const sortedItems = React.useMemo(() => {
    // For server-side pagination, we should use the items directly
    if (config.pagination?.total !== undefined) {
      return items;
    }
    if (!sortDescriptor) {
      return items;
    }

    const sortColumn = config.columns.find(
      (column) => column.uid === sortDescriptor.column
    );
    const sortKey = String(sortColumn?.sortKey ?? sortDescriptor.column ?? '');

    return applySorting(items, {
      column: sortKey,
      direction:
        sortDescriptor.direction === 'descending' ? 'descending' : 'ascending',
    });
  }, [items, sortDescriptor, config.pagination?.total, config.columns]);

  // Handlers
  const normalizeSelection = React.useCallback(
    (keys: Selection, visibleItems: T[]): Selection => {
      if (keys !== 'all') {
        return keys;
      }

      return new Set(visibleItems.map((item) => String(item.id)));
    },
    []
  );

  const handleSelectionChange = (keys: Selection) => {
    const normalizedKeys = normalizeSelection(keys, sortedItems);

    if (controlledSelectedKeys === undefined) {
      setInternalSelectedKeys(normalizedKeys);
    }
    onSelectionChange?.(normalizedKeys);
  };

  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Always call the parent's onPageChange for server-side pagination
    config.pagination?.onPageChange?.(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    // Call parent's onRowsPerPageChange for server-side pagination
    config.pagination?.onRowsPerPageChange?.(newRowsPerPage);
  };

  // Stable so the debounced search input never has to rebuild its timer.
  const handleSearchChange = React.useCallback((value: string) => {
    setFilterValue(value);
    setPage(1);
  }, []);

  const handleFilterChange = (key: string, values: Selection) => {
    userModifiedFiltersRef.current.add(key);
    setFilters((prev) => ({ ...prev, [key]: values }));
    setPage(1);
  };

  React.useEffect(() => {
    config.filters?.forEach((filterConfig) => {
      if (!filterConfig.defaultSelectAll) {
        return;
      }

      const targets = getSelectAllTargetUids(filterConfig);
      if (targets.length === 0) {
        return;
      }

      if (userModifiedFiltersRef.current.has(filterConfig.key)) {
        return;
      }

      setFilters((prev) => ({
        ...prev,
        [filterConfig.key]: new Set(targets),
      }));
    });
  }, [config.filters]);

  const classNames = React.useMemo(
    () => ({
      wrapper: ['max-h-[382px]', 'max-w-3xl'],
      th: ['bg-transparent', 'text-default-500', 'border-b', 'border-divider'],
      tr: ['border-b', 'border-divider', 'data-[last=true]:border-b-0'],
    }),
    []
  );

  const stickyHeaderClassNames = React.useMemo(
    () => ({
      thead: [
        'before:content-[""]',
        'before:absolute',
        'before:-top-4',
        'before:inset-x-0',
        'before:h-4',
        'before:bg-content1',
      ],
    }),
    []
  );

  const isMobile = useMediaQuery('(max-width: 1092px)');
  const useAutoLayout = config.layout === 'auto';
  const tableMaxHeight = config.maxHeight || DEFAULT_MAX_HEIGHT;
  const isStickyHeader = config.stickyHeader ?? !config.withoutBorder;
  const maxHeightClass =
    tableMaxHeight === '600px'
      ? 'max-h-[600px]'
      : tableMaxHeight === '382px'
        ? 'max-h-[382px]'
        : 'max-h-[70vh]';

  const responsiveClassNames = React.useMemo(
    () =>
      isMobile || useAutoLayout
        ? {
            base: 'w-full min-w-0',
            wrapper: 'overflow-x-auto overflow-y-auto',
            table: 'w-full table-auto',
          }
        : {
            base: 'w-full',
            table: 'w-full table-fixed',
          },
    [isMobile, useAutoLayout]
  );

  const cellClassNames = React.useMemo(
    () => ({
      th: cn(
        'px-3 align-middle',
        useAutoLayout ? 'whitespace-nowrap' : 'break-words whitespace-normal',
        config.thClassName
      ),
      td: cn(
        'px-3 py-1 align-middle',
        useAutoLayout ? 'whitespace-nowrap' : 'break-words whitespace-normal',
        config.tdClassName
      ),
    }),
    [useAutoLayout, config.tdClassName, config.thClassName]
  );

  const tableLayout: 'auto' | 'fixed' =
    isMobile || useAutoLayout ? 'auto' : 'fixed';

  const tableProps = React.useMemo(() => {
    if (!config.withoutBorder) {
      return {
        removeWrapper: false,
        isCompact: config.isCompact ?? true,
        isHeaderSticky: false,
        classNames: {
          base: cn('flex w-full flex-col gap-4', responsiveClassNames.base),
          wrapper: [
            'w-full overflow-auto shadow-small',
            !config.disableMaxHeight && maxHeightClass,
            responsiveClassNames.wrapper,
          ].filter(Boolean),
          table: responsiveClassNames.table,
          th: cellClassNames.th,
          td: cellClassNames.td,
          ...(isStickyHeader ? stickyHeaderClassNames : {}),
        },
      };
    }

    return {
      isCompact: true,
      removeWrapper: true,
      classNames: {
        ...classNames,
        base: cn(classNames.wrapper, responsiveClassNames.base),
        table: cn(responsiveClassNames.table),
        th: cn(classNames.th, cellClassNames.th),
        td: cellClassNames.td,
      },
    };
  }, [
    config.withoutBorder,
    classNames,
    stickyHeaderClassNames,
    isMobile,
    responsiveClassNames,
    isStickyHeader,
    maxHeightClass,
    cellClassNames,
    useAutoLayout,
  ]);

  const topContent = (
    <TopContent
      config={config}
      filterValue={filterValue}
      visibleColumns={visibleColumns}
      filters={filters}
      onSearchChange={handleSearchChange}
      onFilterChange={handleFilterChange}
      onVisibleColumnsChange={setVisibleColumns}
      totalItems={config.pagination?.total || safeData.length}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );

  if (config.virtualization) {
    return (
      <div className={cn('w-full min-w-0', config.className)}>
        <VirtualizedTable<T>
          columns={headerColumns}
          items={sortedItems}
          virtualization={config.virtualization}
          selectionMode={getSelectionMode(config)}
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
          sortDescriptor={sortDescriptor}
          onSortChange={handleSortChange}
          isLoading={isLoading}
          topContent={topContent}
          tdClassName={config.tdClassName}
          ariaLabel={config.title}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full min-w-0',
        config.withoutBorder && 'flex h-full min-h-0 flex-1 flex-col',
        config.withoutBorder && isMobile && !useAutoLayout && 'overflow-x-auto',
        config.className
      )}
    >
      <Table
        {...tableProps}
        layout={tableLayout}
        isCompact
        isHeaderSticky={isStickyHeader}
        aria-label={config.title || 'Data Table'}
        bottomContentPlacement={config.bottomContentPlacement || 'outside'}
        topContentPlacement="outside"
        selectionMode={getSelectionMode(config)}
        selectedKeys={selectedKeys}
        sortDescriptor={sortDescriptor}
        topContent={topContent}
        bottomContent={
          <BottomContent
            selectedKeys={selectedKeys}
            filteredItems={filteredItems}
            page={config.pagination?.page || page}
            pages={Math.ceil(
              (config.pagination?.total || safeData.length) / rowsPerPage
            )}
            onPageChange={handlePageChange}
            config={config}
          />
        }
        onSelectionChange={handleSelectionChange}
        onSortChange={handleSortChange}
      >
        <TableHeader columns={headerColumns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              align={column.align || 'start'}
              allowsSorting={column.sortable}
              width={getColumnWidth(
                column,
                headerColumns.length,
                isMobile,
                useAutoLayout
              )}
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          items={sortedItems}
          emptyContent="No data to display"
          isLoading={isLoading}
          loadingContent={<Spinner className="fixed top-1/2 left-1/2" />}
        >
          {(item) => (
            <TableRow key={item.id} className="hover:bg-default-100">
              {(columnKey) => {
                const column = config.columns.find(
                  (col) => col.uid === columnKey
                );
                return (
                  <TableCell
                    className={
                      column?.wrap
                        ? '!whitespace-normal break-words align-middle'
                        : undefined
                    }
                  >
                    {column?.renderCell
                      ? column.renderCell(item)
                      : String(item[columnKey as keyof T])}
                  </TableCell>
                );
              }}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
